import { CreditCardData, TokenData, BinInformation, InstallmentPlanInformation } from '../interfaces';
import { ApiErrorResponse } from '../interfaces/api-error';
import { getDataErrors } from '../utils/validation';
import { tokenize, getBinInformation, getInstallmentsPlan, logToServer } from '../services';
import { formatLongYear, formatMonth, removeNaNAndEmpty } from '../utils/formatter';
import { ERRORS } from '../constants';
import { AxiosError } from 'axios';

function getFetchTokenData(apiKey: string, data: CreditCardData): TokenData {
    const body = {
        expiration_month: formatMonth(data.expirationMonth),
        expiration_year: formatLongYear(data.expirationYear),
        holder_name: data.name,
        pan: removeNaNAndEmpty(data.pan),
        cvv: removeNaNAndEmpty(data.cvv),
        key: apiKey,
        country_code: data.country
    };
    return body;
}

export async function fetchToken(apiKey: string, data: CreditCardData): Promise<{ token: string } | { error: string }> {
    try {
        const dataError = await getDataErrors(data);

        if (dataError) {
            await logToServer(apiKey, `Error validating data`, 'error', { dataError });
            return Promise.reject({ error: dataError });
        }
        const tokenData = getFetchTokenData(apiKey, data);
        const token = (await tokenize(apiKey, tokenData)).data.token;

        await logToServer(apiKey, `Token fetched successfully`, 'info');

        return Promise.resolve({ token });
    } catch (error) {
        let errorMessage = { message: ERRORS().TOKENIZE_ERROR };
        await logToServer(apiKey, `Error fetching token`, 'error', { errorMessage });
        return Promise.reject({ error: errorMessage });
    }
}

export async function fetchBinInformation(apiKey: string, bin: string, country: string): Promise<BinInformation> {
    try {
        const binInformation = await getBinInformation(apiKey, bin, country);
        return binInformation;
    } catch (e) {
        const error = e as AxiosError<ApiErrorResponse>;
        if (error.response?.data?.code === 'INVALID_GET_BIN_REQUEST') {
            const errorMessage = { number: ERRORS(country).CCNUMBER_ERROR };
            return Promise.reject({ error: errorMessage });
        }
        const errorMessage = { message: ERRORS().BIN_ERROR };
        return Promise.reject({ error: errorMessage });
    }
}

export async function fetchInstallmentsPlan(apiKey: string, amount: number, currency: string, country: string, bin: string): Promise<InstallmentPlanInformation> {
    try {
        const installmentsPlan = await getInstallmentsPlan(apiKey, amount, currency, country, bin);
        return installmentsPlan;
    } catch (error) {
        const errorMessage = { message: ERRORS().INSTALLMENTS_ERROR };
        return Promise.reject({ error: errorMessage });
    }
}
